import posthog from 'posthog-js'

import { logger } from '@/utils/logger'

/**
 * Unified analytics event hub.
 *
 * One call fans out to GA4, PostHog, Google Ads conversions,
 * LinkedIn conversions, Reddit conversions, Bing UET events, and Meta Pixel.
 * Bing UET, LinkedIn Insight Tag, Reddit Pixel, and Meta Pixel also auto-track page views.
 *
 * UTM attribution:
 * - PostHog captures UTMs natively (both $initial_* and latest person properties,
 *   plus click IDs like gclid, fbclid, msclkid, li_fat_id, rdt_cid)
 * - GA4 handles UTMs natively via session-level tracking
 * - HubSpot gets UTMs via hidden form fields in useHubSpotForm.ts (Sourcebuster)
 *
 * Usage:
 *   trackEvent('Demo Requested', { email: 'user@co.com', company: 'Acme' })
 *
 * To add a new event:
 *   1. Add it to EVENT_CONFIG below
 *   2. Call trackEvent() from your component
 *   3. For Bing: create a matching Conversion Goal in Microsoft Ads
 *      (Tools → Conversion tracking → Conversion goals → Create → Event)
 *      with the event_action matching the bingAction value below.
 *
 * Conversion values: Google Ads values are managed in the dashboard.
 * Meta Pixel values are sent in-code (Meta requires value + currency in the event payload).
 */

const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID
const GA4_ID = process.env.NEXT_PUBLIC_GA4_ID

interface IdentifyUserOptions {
  phone?: string
}

interface RedditConversion {
  /** Reddit standard event type (e.g. 'Lead') or 'Custom' for custom events */
  type: string
  /** Required when type is 'Custom' — the custom event name in Reddit Ads Manager */
  customEventName?: string
}

interface MetaConversion {
  /** 'standard' => fbq('track', name), 'custom' => fbq('trackCustom', name) */
  type: 'standard' | 'custom'
  /** Meta standard event name or custom event name */
  name: string
  /** Conversion value — Meta requires this for standard events (Lead, Schedule, etc.) */
  value: number
  /** ISO 4217 currency code */
  currency: string
}

interface BingEvent {
  action: string
  params?: Record<string, unknown>
}

interface EventConfig {
  /** GA4 event name — fires gtag('event', name, { send_to: GA4_ID }). Mark as key event in GA4 Admin for conversions. */
  ga4EventName?: string
  gadsLabel?: string
  /** LinkedIn conversion ID — each event type has its own ID from LinkedIn Campaign Manager */
  linkedinConversionId?: number
  /** Reddit conversion event — fires rdt('track', type) or rdt('track', 'Custom', { customEventName }) */
  redditConversion?: RedditConversion
  /** Bing UET event action — fires uetq.push('event', action, {...}). Create a matching Conversion Goal in Microsoft Ads. */
  bingAction?: string
  /** Optional additional Bing UET actions (e.g. legacy aliases). */
  bingAdditionalEvents?: BingEvent[]
  /** Meta Pixel conversion mapping. */
  metaConversion?: MetaConversion
  /**
   * OpenAI / ChatGPT Ads conversion — fires
   * oaiq('measure', name, { type: 'customer_action', amount, currency: 'USD' }).
   * `name` is an OpenAI standard event (e.g. appointment_scheduled, lead_created).
   */
  openaiEvent?: { name: string; amount?: number }
}

function removeAccents(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/**
 * Normalize email for ad-platform enhanced matching:
 * - trim + lowercase
 * - remove whitespace + accents
 * - normalize plus/dot aliases for gmail/googlemail local parts
 * - require valid local@domain
 */
export function normalizeEmailForEnhancedMatching(email: string): string {
  const sanitized = removeAccents(email).replace(/\s+/g, '').toLowerCase()

  const atIndex = sanitized.indexOf('@')
  if (atIndex <= 0 || atIndex !== sanitized.lastIndexOf('@') || atIndex === sanitized.length - 1) return ''

  let local = sanitized.slice(0, atIndex)
  let domain = sanitized.slice(atIndex + 1)

  local = local.replace(/\.+$/, '')
  domain = domain.replace(/\.+$/, '')

  const isGoogleMailbox = domain === 'gmail.com' || domain === 'googlemail.com'
  if (isGoogleMailbox) {
    local = local.replace(/\+.*/, '').replace(/\./g, '')
  }

  if (!local || !domain) return ''
  return `${local}@${domain}`
}

/**
 * Normalize phone to E.164.
 * - Accepts +<country><number>
 * - Converts 00<country><number> to +<country><number>
 * - Best-effort US default for 10-digit NANP numbers (+1)
 * - Returns undefined when we cannot confidently normalize
 */
function normalizePhoneForEnhancedMatching(phone?: string): string | undefined {
  if (!phone) return undefined

  const trimmed = phone.trim()
  if (!trimmed) return undefined

  const withoutExtension = trimmed.replace(/\s*(?:ext\.?|x)\s*\d+$/i, '')
  const hadPlusPrefix = withoutExtension.trim().startsWith('+')
  const digitsOnly = withoutExtension.replace(/\D/g, '')

  if (!digitsOnly) return undefined

  if (hadPlusPrefix) {
    if (digitsOnly.length < 8 || digitsOnly.length > 15) return undefined
    return `+${digitsOnly}`
  }

  if (digitsOnly.startsWith('00')) {
    const internationalDigits = digitsOnly.slice(2)
    if (internationalDigits.length < 8 || internationalDigits.length > 15) return undefined
    return `+${internationalDigits}`
  }

  // Best-effort handling for US forms that omit country code.
  if (digitsOnly.length === 10) return `+1${digitsOnly}`
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) return `+${digitsOnly}`

  return undefined
}

/**
 * Central event configuration.
 * Maps a human-readable event name to its conversion destinations.
 *
 * Conversion values are set in Google Ads dashboard (Goals → Conversions):
 *   Lead (Demo Requested) = $1,660.50
 *   Partner Demo Requested = $100.00
 *   Download Resource = $100.00
 */
const EVENT_CONFIG: Record<string, EventConfig> = {
  // HubSpot form events — ga4EventName values match existing GA4 key events
  'Resource Download Clicked': {
    ga4EventName: 'Resource_Download_Clicked',
    gadsLabel: 'RqbfCPW66IMDELjHwboC',
    linkedinConversionId: 5668028,
    redditConversion: { type: 'Custom', customEventName: 'Resource Download' },
    bingAction: 'resource_download',
    metaConversion: { type: 'custom', name: 'ResourceDownload', value: 25, currency: 'USD' },
  },
  'Demo Requested': {
    ga4EventName: 'Demo_Requested',
    gadsLabel: 'YL4ECLfi-ckBELjHwboC',
    openaiEvent: { name: 'lead_created', amount: 100 },
    linkedinConversionId: 8591521,
    redditConversion: { type: 'Lead' },
    bingAction: 'demo_requested',
    bingAdditionalEvents: [{ action: 'track', params: { event_label: 'demo' } }],
    metaConversion: { type: 'standard', name: 'Lead', value: 100, currency: 'USD' },
  },
  'Partner Demo Requested': {
    ga4EventName: 'Partner_Demo_Requested',
    gadsLabel: 'asQYCM2cw_EZELjHwboC',
    linkedinConversionId: 18466180,
    redditConversion: { type: 'Custom', customEventName: 'Partner Lead' },
    bingAction: 'partner_demo_requested',
    metaConversion: { type: 'standard', name: 'Lead', value: 25, currency: 'USD' },
  },
  'Ebook Download Clicked': {
    ga4EventName: 'Ebook_Download_Clicked',
    gadsLabel: 'RqbfCPW66IMDELjHwboC',
    linkedinConversionId: 5668028,
    redditConversion: { type: 'Custom', customEventName: 'Resource Download' },
    bingAction: 'resource_download',
    metaConversion: { type: 'custom', name: 'ResourceDownload', value: 25, currency: 'USD' },
  },

  // Blog/content events
  'Recommended Reading Clicked': {
    ga4EventName: 'Recommended_Reading_Clicked',
  },

  // Newsletter — opt-in forms (blog sidebar, hub articles, glossary, newsroom) and footer email capture
  'Newsletter Subscribed': {
    ga4EventName: 'Newsletter_Subscribed',
  },

  // Swag form
  'Swag Requested': {
    ga4EventName: 'Swag_Requested',
  },

  // Contact form
  'Contact Us Submitted': {
    ga4EventName: 'Contact_Us_Submitted',
  },

  // CMMC Readiness Assessment — email gate (optional Zapier forward via internal API)
  'CMMC Readiness Gate Submitted': {
    ga4EventName: 'CMMC_Readiness_Gate_Submitted',
  },

  // Event registration (e.g. /summit)
  // Google Ads conversion value: $25 (set in Google Ads dashboard → Goals → Conversions)
  'Event Registered': {
    ga4EventName: 'Event_Registered',
    gadsLabel: '0jeOCKmpgZQcELjHwboC',
    linkedinConversionId: 24950588,
    redditConversion: { type: 'Custom', customEventName: 'Event Registration' },
    bingAction: 'event_registered',
    metaConversion: { type: 'standard', name: 'CompleteRegistration', value: 25, currency: 'USD' },
  },

  // ChiliPiper meeting booked — fires from onSuccess callback after scheduling
  // ga4EventName matches existing GA4 key event 'Meeting Booked' (created in GA4 Admin, previously unfed)
  // Value: $1,500 (secondary action — next step after Demo Requested, configured in ad platform dashboards)
  'Meeting Booked': {
    ga4EventName: 'Meeting Booked',
    gadsLabel: 'gzkrCODD6f4bELjHwboC',
    openaiEvent: { name: 'appointment_scheduled', amount: 200 },
    linkedinConversionId: 24392404,
    bingAction: 'meeting_booked',
    metaConversion: { type: 'standard', name: 'Schedule', value: 200, currency: 'USD' },
  },
}

/**
 * Track an event across all analytics destinations in one call.
 *
 * @param eventName - The canonical event name (key in EVENT_CONFIG)
 * @param data - Event properties (sent to PostHog as event-level properties, not $set)
 * @param options - skipPostHog to suppress PostHog capture; skipMeta to suppress the
 *   Meta Pixel event (use for deferred conversions whose advanced-matching snapshot was
 *   empty — Meta advanced matching can't be cleared once set, so firing would attach a
 *   previous visitor's identifiers, MAR-882)
 */
export function trackEvent(
  eventName: string,
  data?: Record<string, unknown>,
  options?: { skipPostHog?: boolean; skipMeta?: boolean },
) {
  const config = EVENT_CONFIG[eventName]
  // Pixels without a consent API (LinkedIn, Reddit, OpenAI) get no events after a marketing opt-out;
  // gtag, Bing UET and Meta apply their own consent state (see utils/consent.ts).
  const adsConsented = typeof window !== 'undefined' && window.__sfConsent?.ads !== false

  // Each destination is wrapped in its own try/catch so a failure in one
  // (e.g. ad blocker, SDK not loaded) never prevents the others from firing.

  // PostHog — data sent as event properties for per-event analysis.
  // UTM attribution is handled natively by PostHog (no Sourcebuster needed).
  if (!options?.skipPostHog) {
    try {
      posthog.capture(eventName, data)
    } catch (error) {
      logger.error('[Analytics] PostHog capture failed:', {
        eventName,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // GA4 event — fires named event with proper session attribution
  if (config?.ga4EventName && GA4_ID && typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', config.ga4EventName, {
        send_to: GA4_ID,
        event_category: 'form_submission',
        event_label: eventName,
      })
    } catch (error) {
      logger.error('[Analytics] GA4 event failed:', {
        eventName,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Google Ads conversion
  if (config?.gadsLabel && GOOGLE_ADS_ID && typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_ID}/${config.gadsLabel}`,
      })
    } catch (error) {
      logger.error('[Analytics] Google Ads conversion failed:', {
        eventName,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // LinkedIn conversion
  if (config?.linkedinConversionId && adsConsented && window.lintrk) {
    try {
      window.lintrk('track', { conversion_id: config.linkedinConversionId })
    } catch (error) {
      logger.error('[Analytics] LinkedIn conversion failed:', {
        eventName,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Reddit conversion
  if (config?.redditConversion && adsConsented && window.rdt) {
    try {
      if (config.redditConversion.customEventName) {
        window.rdt('track', config.redditConversion.type, {
          customEventName: config.redditConversion.customEventName,
        })
      } else {
        window.rdt('track', config.redditConversion.type)
      }
    } catch (error) {
      logger.error('[Analytics] Reddit conversion failed:', {
        eventName,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Bing UET custom event
  if (config?.bingAction && typeof window !== 'undefined' && window.uetq) {
    try {
      window.uetq.push('event', config.bingAction, {
        event_category: 'form_submission',
        event_label: eventName,
      })

      for (const bingEvent of config.bingAdditionalEvents ?? []) {
        window.uetq.push('event', bingEvent.action, bingEvent.params ?? {})
      }
    } catch (error) {
      logger.error('[Analytics] Bing UET event failed:', {
        eventName,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Meta Pixel conversion — value + currency required for standard events (Lead, Schedule, etc.)
  // Skipped when skipMeta is set (deferred conversion with no fresh advanced-matching).
  if (config?.metaConversion && !options?.skipMeta && typeof window !== 'undefined' && window.fbq) {
    try {
      const metaParams = { value: config.metaConversion.value, currency: config.metaConversion.currency }
      if (config.metaConversion.type === 'custom') {
        window.fbq('trackCustom', config.metaConversion.name, metaParams)
      } else {
        window.fbq('track', config.metaConversion.name, metaParams)
      }
    } catch (error) {
      logger.error('[Analytics] Meta Pixel conversion failed:', {
        eventName,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // OpenAI / ChatGPT Ads conversion
  if (config?.openaiEvent && adsConsented && window.oaiq) {
    try {
      window.oaiq('measure', config.openaiEvent.name, {
        type: 'customer_action',
        amount: config.openaiEvent.amount ?? 0,
        currency: 'USD',
      })
    } catch (error) {
      logger.error('[Analytics] OpenAI conversion failed:', {
        eventName,
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }
}

/**
 * Normalized identifiers (email/phone) passed to ad-platform enhanced-conversion
 * buffers. Platforms hash them client-side before sending.
 */
export type EnhancedIdentifiers = { email?: string; phone?: string }

// Identifiers from the most recent identifyUser(). Exposed via
// getPendingEnhancedUserData() so a deferred conversion (ChiliPiper 'Meeting
// Booked') can SNAPSHOT the submitter's identifiers when the booking widget opens,
// instead of reading this mutable value at success time — which a later, unrelated
// form submit could have overwritten (MAR-882 review).
let pendingEnhancedUserData: EnhancedIdentifiers | null = null

/**
 * Single writer for every ad platform's enhanced-conversion buffer.
 * - `ids` with an email/phone → (re)sets gtag / Bing UET / Meta (hashed client-side).
 * - `null` → clears gtag + Bing UET (an empty payload replaces prior values).
 *   Meta advanced matching cannot be reliably un-set once fbq('init') ran, so we
 *   do NOT fake a clear — we only (re)set Meta when we have data. The next valid
 *   identify overwrites it, and a conversion only fires right after its own submit,
 *   so the stale window is bounded.
 */
function writeEnhancedUserData(ids: EnhancedIdentifiers | null) {
  if (typeof window === 'undefined') return
  const email = ids?.email
  const phone = ids?.phone

  // Google Ads Enhanced Conversions — gtag hashes client-side (SHA-256); an empty
  // object replaces (clears) prior user_data. Set before gtag('event','conversion').
  if (window.gtag) {
    try {
      window.gtag('set', 'user_data', {
        ...(email ? { email } : {}),
        ...(phone ? { phone_number: phone } : {}),
      })
    } catch (error) {
      logger.error('[Analytics] gtag user_data write failed:', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Bing UET — empty pid replaces prior identifiers.
  if (window.uetq) {
    try {
      window.uetq.push('set', {
        pid: {
          ...(email ? { em: email } : {}),
          ...(phone ? { ph: phone } : {}),
        },
      })
    } catch (error) {
      logger.error('[Analytics] Bing UET user data write failed:', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // Meta Advanced Matching — only (re)set when we have data (no honest clear path).
  if (window.fbq && (email || phone)) {
    try {
      window.fbq('init', process.env.NEXT_PUBLIC_META_PIXEL_ID, {
        ...(email ? { em: email } : {}),
        ...(phone ? { ph: phone } : {}),
      })
    } catch (error) {
      logger.error('[Analytics] Meta Advanced Matching failed:', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }
}

/**
 * Clears the pending identifiers and wipes the gtag/UET enhanced-conversion
 * buffers. Call when a submit yields no valid identifier (see writeEnhancedUserData
 * for the Meta caveat).
 */
export function clearEnhancedUserData() {
  pendingEnhancedUserData = null
  writeEnhancedUserData(null)
}

/** Snapshot of the most recent submit's normalized identifiers (or null). */
export function getPendingEnhancedUserData(): EnhancedIdentifiers | null {
  return pendingEnhancedUserData
}

/**
 * Applies an EXPLICIT identifier snapshot to the ad-platform buffers immediately
 * before a deferred conversion fires (notably ChiliPiper 'Meeting Booked'). Pass
 * the identifiers captured when the booking started (getPendingEnhancedUserData in
 * onRouted) so the conversion carries THAT visitor's data — never whatever a later
 * submit left behind. Pass null to send no identifiers (MAR-882 review).
 */
export function applyEnhancedUserData(ids: EnhancedIdentifiers | null) {
  writeEnhancedUserData(ids)
}

/**
 * Identify a user across analytics destinations.
 * Call on form submit when email is available.
 */
export function identifyUser(
  email: string,
  traits?: Record<string, unknown>,
  options?: IdentifyUserOptions,
): EnhancedIdentifiers | null {
  const identityEmail = email.trim().toLowerCase()
  if (!identityEmail) {
    clearEnhancedUserData()
    return null
  }

  const enhancedEmail = normalizeEmailForEnhancedMatching(email)
  const traitsPhone =
    typeof traits?.phone === 'string'
      ? traits.phone
      : typeof traits?.phoneNumber === 'string'
        ? traits.phoneNumber
        : undefined
  const normalizedPhone = normalizePhoneForEnhancedMatching(options?.phone ?? traitsPhone)

  try {
    posthog.identify(identityEmail, traits)
  } catch (error) {
    logger.error('[Analytics] PostHog identify failed:', {
      error: error instanceof Error ? error.message : String(error),
    })
  }

  if (!enhancedEmail && !normalizedPhone) {
    // Current submit yielded no usable identifier (e.g. email failed normalization) —
    // clear any prior session data so a later conversion can't inherit an earlier
    // visitor's email (MAR-882 review).
    clearEnhancedUserData()
    logger.warn('[Analytics] Enhanced conversion identifiers unavailable after normalization')
    return null
  }

  // Capture the normalized identifiers for this submit, set them on every ad
  // platform's enhanced-conversion buffer (for conversions firing now, e.g. Demo
  // Requested), and return them so a deferred conversion can snapshot them.
  const ids: EnhancedIdentifiers = {
    email: enhancedEmail || undefined,
    phone: normalizedPhone || undefined,
  }
  pendingEnhancedUserData = ids
  writeEnhancedUserData(ids)
  return ids
}
